import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const WordHuntGame = ({ gameData, onComplete }) => {
    const [foundWords, setFoundWords] = useState([]);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [textParts, setTextParts] = useState([]);

    useEffect(() => {
        if (!gameData?.text) return;
        const words = gameData.text.split(/(\s+)/);
        setTextParts(words);
    }, [gameData]);

    const cleanWord = (word) => word.toLowerCase().replace(/[.,!?;:()]/g, '');

    const handleWordClick = (word, index) => {
        if (feedback) return;
        const cleanedClicked = cleanWord(word);
        if (!cleanedClicked || foundWords.includes(cleanedClicked)) return;

        const isTarget = gameData.target_words.some(target => cleanWord(target) === cleanedClicked);

        if (isTarget) {
            setFeedback('correct');
            const newFound = [...foundWords, cleanedClicked];
            setFoundWords(newFound);
            const wordPoints = gameData.points_per_word || 15;
            const newScore = score + wordPoints;
            setScore(newScore);
            triggerHaptic(60);

            setTimeout(() => {
                setFeedback(null);
                const allTargetsFound = gameData.target_words.every(target => newFound.includes(cleanWord(target)));
                if (allTargetsFound) {
                    triggerHaptic([200, 100, 200]);
                    setTimeout(() => onComplete?.(newScore), 3000);
                }
            }, 2500);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => setFeedback(null), 1500);
        }
    };

    if (!gameData || !gameData.text || !gameData.target_words) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-emerald-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🔦</div>
                <h3 className="text-base md:text-3xl font-black text-emerald-900 tracking-tighter uppercase">Slepiami žodžiai...</h3>
            </div>
        );
    }

    const progress = (foundWords.length / gameData.target_words.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-emerald-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-emerald-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        🔦
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Žodžių Medžioklė
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Taškai</p>
                        <p className="text-sm md:text-2xl font-black text-emerald-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Rasta</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{foundWords.length}/{gameData.target_words.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(16,185,129,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-8 text-center border-white/60 shadow-2xl bg-white/40 backdrop-blur-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-2 md:p-6 text-6xl opacity-[0.03] select-none pointer-events-none text-emerald-900">📝</div>
                <h4 className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.5em] mb-4 md:mb-8">Radinių Sąrašas</h4>
                <div className="flex flex-wrap justify-center gap-2 md:gap-4">
                    {gameData.target_words.map((word, index) => {
                        const isFound = foundWords.includes(cleanWord(word));
                        return (
                            <div
                                key={index}
                                className={`
                                    px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-[2rem] text-sm md:text-2xl font-black transition-all duration-500 flex items-center gap-2 md:gap-4 border-2
                                    ${isFound
                                        ? 'bg-emerald-500 text-white border-emerald-400 shadow-xl scale-105'
                                        : 'bg-white text-slate-300 border-dashed border-slate-200 opacity-60'}
                                `}
                            >
                                <span className="tracking-tighter uppercase">{word}</span>
                                {isFound && <span className="text-xs md:text-xl animate-pop-in">✅</span>}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 shadow-2xl border-white/60 bg-slate-900 relative overflow-hidden flex flex-col items-center min-h-[400px]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(16,185,129,0.08)_0%,transparent_80%)] pointer-events-none" />
            <div className="absolute top-0 left-0 p-4 text-6xl opacity-[0.05] select-none pointer-events-none text-white">🔦</div>
            
            <div className="bg-slate-800/60 rounded-xl md:rounded-[3rem] p-6 md:p-12 shadow-inner border-4 border-slate-700 relative z-10 leading-[3] md:leading-[4] text-lg md:text-4xl font-black text-white font-sans text-center break-words select-text w-full max-w-4xl">
                {textParts.map((part, index) => {
                    const cleaned = cleanWord(part);
                    const isFound = foundWords.includes(cleaned);
                    const isSpace = /^\s+$/.test(part);

                    if (isSpace) return <span key={index}>{part}</span>;

                    return (
                        <button
                            key={index}
                            onClick={() => handleWordClick(part, index)}
                            disabled={isFound || !!feedback}
                            className={`
                                inline-block px-3 py-0.5 md:px-5 md:py-1 rounded-xl md:rounded-[2rem] transition-all duration-300 transform border-2
                                ${isFound
                                    ? 'bg-emerald-500 text-white border-emerald-400 shadow-[0_0_30px_rgba(16,185,129,0.4)] scale-110'
                                    : 'bg-transparent border-transparent hover:bg-white/10 hover:border-emerald-500/30 active:scale-95 text-slate-300 hover:text-white'}
                            `}
                        >
                            {part}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-8 md:p-16 rounded-[3rem] md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '🔦' : '🔍'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-2xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'RADAI!' : 'IEŠKOK TOLIAU!'}
                            </h2>
                            <div className={`p-4 md:p-8 rounded-2xl border-2 italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-xl font-bold text-slate-700 text-center">
                                    {feedback === 'correct' ? 'Puikiai pastebėjai paslėptą žodį!' : 'Šio žodžio mūsų sąraše nėra.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default WordHuntGame;
